import axios from "axios";
import { db } from "../utils/firebase"; 
import { collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Chat from "../models/Chat.js";

const API_BASE_URL = "https://europe-west3-hyper-e122e.cloudfunctions.net";

export function authStateReady() {
  return new Promise((resolve) => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

class ChatsService {

  static async uploadChatFile(file, chatId) {
    const user = await authStateReady();
  
    if (!user) {
      throw new Error("Пользователь не авторизован");
    }
  
    if (!chatId) {
      throw new Error("chat_id обязателен для загрузки файла");
    }
  
    const token = await user.getIdToken(true);
  
    const formData = new FormData();
    formData.append("user_id", user.uid);
    formData.append("chat_id", chatId);
    formData.append("file", file);
  
    const response = await axios.post(
      `${API_BASE_URL}/upload_chat_file`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
  
    return response.data;
  }

    /**
   * Получение списка чатов для пользователя
   * @param {string} userId — ID текущего пользователя
   * @returns {Promise<Array>} Массив чатов
   */
    static async getMyChats() {
      try {
        const user = await authStateReady();
        if (!user) {
          throw new Error("Пользователь не авторизован");
        }
        const token = await user.getIdToken();
        const response = await axios.get(`${API_BASE_URL}/get_my_chats`, {
          params: { user_id: user.uid },
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          }
        });
        console.log("✅ Чаты успешно получены:", response.data);
        return (response.data.chats || [])
          .map((chat) => Chat.fromJson(chat))
          .filter(Boolean);
      } catch (error) {
        console.error("❌ Ошибка получения чатов:", error);
        return [];
      }
    }


  /**
   * Получение сообщений чата с пагинацией
   * @param {string} chatId — ID чата
   * @param {number} limit — Количество сообщений за один запрос
   * @param {string|null} lastMessageId — ID последнего сообщения для пагинации (опционально)
   * @returns {Promise<{messages: Array, nextLastMessageId: string|null}>}
   */
  static async getChatMessages(chatId, limit = 20, lastMessageId = null, interlocutorId = null) {

    try {
  
      if (!chatId && !interlocutorId) {
  
        console.warn("📭 Нет chatId и interlocutorId");
        return {
          messages: [],
          nextLastMessageId: null,
          isOnline: false,
          is_new_chat: true
        };
      }
  
      const user = await authStateReady();
  
      if (!user) {
  
        throw new Error("Пользователь не авторизован");
  
      }
  
      const token = await user.getIdToken();
  
      const params = {
        limit,
        ...(chatId ? { chat_id: chatId } : {}),
        ...(interlocutorId ? { interlocutor_id: interlocutorId } : {}), 
        ...(lastMessageId ? { last_message_id: lastMessageId } : {})
  
      };
  
      const response = await axios.get(`${API_BASE_URL}/get_chat_messages`, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log("✅ Сообщения успешно получены:", response.data);
      
      console.log("📦 FULL get_chat_messages RESPONSE:");
      console.log(JSON.stringify(response.data, null, 2));
      
      return {
        messages: response.data.messages || [],
        nextLastMessageId: response.data.next_last_message_id || null,
        isOnline: response.data.is_online ?? false,
        is_new_chat: response.data.is_new_chat ?? false,
        chatId: response.data.chat_id ?? null,
        chatType: response.data.chat_type ?? null
      };
  
    } catch (error) {
      console.error("❌ Ошибка получения сообщений:", error);
      return {
        messages: [],
        nextLastMessageId: null,
        isOnline: false,
        is_new_chat: true
      };
    }
  }

/**
 * Отправка нового сообщения в чат
 * @param {Object} payload
 * @returns {Promise<{success: boolean, messageId: string|null, chat_id?: string, error?: string}>}
 */
static async sendMessage(payload) {
  console.log("📤 Отправка сообщения в чат...", payload);

  try {
    const user = await authStateReady();

    if (!user) {
      throw new Error("Пользователь не авторизован");
    }

    const token = await user.getIdToken();

    const requestPayload = {
      ...payload,
      sender_id: user.uid,
      text_message: payload.text_message ?? ""
    };

    const hasText =
      typeof requestPayload.text_message === "string" &&
      requestPayload.text_message.trim().length > 0;

    const hasAttachment =
      typeof requestPayload.attachment_url === "string" &&
      requestPayload.attachment_url.trim().length > 0;

    if (!hasText && !hasAttachment) {
      throw new Error("Нужно передать текст или файл");
    }

    const response = await axios.post(
      `${API_BASE_URL}/send_message`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }
    );

    console.log("✅ Сообщение успешно отправлено:", response.data);

    return {
      success: true,
      messageId: response.data.message_id ?? null,
      message_id: response.data.message_id ?? null,
      chat_id: response.data.chat_id ?? null,
      chatId: response.data.chat_id ?? null
    };

  } catch (error) {
    console.error("❌ Ошибка отправки сообщения:", error);

    return {
      success: false,
      messageId: null,
      message_id: null,
      chat_id: null,
      chatId: null,
      error: error.response?.data?.error || error.message
    };
  }
}

/**
 * Слушатель новых сообщений в чате
 * @param {string} chatId — ID чата
 * @param {Function} callback — Функция-обработчик, вызываемая при новом сообщении
 */
static listenForNewMessages(chatId, callback) {
  if (!chatId) {
    console.warn("⚠️ listenForNewMessages: chatId пустой");
    return () => {};
  }

  const messagesRef = collection(db, "Messages");

  console.log("Подключаем слушатель для сообщений в чате:", chatId);

  const q = query(
    messagesRef,
    where("id", "==", chatId),
    orderBy("send_date", "desc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    console.log("Получили данные от Firestore:", snapshot.docs.length);

    const newMessages = snapshot.docs.map(doc => {
      const data = doc.data();

      return {
        id: doc.id,

        textMessage: data.text_message || "",
        text_message: data.text_message || "",

        sendDate: data.send_date
          ? data.send_date.toDate().toISOString()
          : null,
        send_date: data.send_date
          ? data.send_date.toDate().toISOString()
          : null,

        senderId: data.sender_id || "",
        sender_id: data.sender_id || "",

        chatId: data.id || "",
        chat_id: data.id || "",

        isRead: data.is_read ?? data.was_read ?? false,
        is_read: data.is_read ?? data.was_read ?? false,

        is_deleted: data.is_deleted ?? false,

        imageUrl: data.image_url || null,
        image_url: data.image_url || null,

        messageType: data.message_type || null,
        message_type: data.message_type || null,

        attachmentUrl: data.attachment_url || null,
        attachment_url: data.attachment_url || null,

        attachmentName: data.attachment_name || null,
        attachment_name: data.attachment_name || null,

        attachmentMimeType: data.attachment_mime_type || null,
        attachment_mime_type: data.attachment_mime_type || null,

        attachmentSize: data.attachment_size || null,
        attachment_size: data.attachment_size || null,

        storagePath: data.storage_path || null,
        storage_path: data.storage_path || null,

        attachmentSize: data.attachment_size || null,
        attachment_size: data.attachment_size || null,

        storagePath: data.storage_path || null,
        storage_path: data.storage_path || null,

        videoConferenceId: data.video_conference_id || null,
        video_conference_id: data.video_conference_id || null,

        videoProvider: data.video_provider || null,
        video_provider: data.video_provider || null,

        videoProviderConferenceId:
          data.video_provider_conference_id || null,
        video_provider_conference_id:
          data.video_provider_conference_id || null,

        videoJoinUrl: data.video_join_url || null,
        video_join_url: data.video_join_url || null,

        videoTitle: data.video_title || null,
        video_title: data.video_title || null,

        videoStatus: data.video_status || null,
        video_status: data.video_status || null
      };
    });

    console.log("Новые сообщения:", newMessages);

    if (newMessages.length > 0) {
      console.log("Новое сообщение:", newMessages[0]);
      callback(newMessages[0]);
    }
  }, (error) => {
    console.error("Ошибка при прослушивании сообщений:", error);
  });

  return unsubscribe;
}

static listenForAllNewMessages(chatIds, callback) {
  if (!Array.isArray(chatIds) || chatIds.length === 0) {
    console.warn("⚠️ listenForAllNewMessages: chatIds пустой");
    return () => {};
  }

  const unsubscribers = chatIds.map((chatId) => {
    const messagesRef = collection(db, "Messages");

    const q = query(
      messagesRef,
      where("id", "==", chatId),
      orderBy("send_date", "desc"),
      limit(1)
    );

    return onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== "added" && change.type !== "modified") return;

        const data = change.doc.data();

        const message = {
          id: change.doc.id,
          textMessage: data.text_message || "",
          sendDate: data.send_date?.toDate?.().toISOString() || null,
          senderId: data.sender_id || "",
          chatId: data.id || "",

          isRead: data.is_read ?? data.was_read ?? false,
          imageUrl: data.image_url || null,
          attachmentUrl: data.attachment_url || null,
          attachmentName: data.attachment_name || null,
          attachmentMimeType: data.attachment_mime_type || null,
          attachmentSize: data.attachment_size || null,
          storagePath: data.storage_path || null,
        };

        console.log("📬 Новое сообщение в чате:", message);
        callback(message);
      });
    });
  });

  return () => {
    unsubscribers.forEach(unsubscribe => unsubscribe());
  };
}


/**
 * Отметить сообщения как прочитанные (was_read = true)
 * @param {string[]} messageIds — Массив ID сообщений
 * @returns {Promise<{success: boolean}>}
 */
static async markMessagesAsRead(messageIds) {
  try {
    if (!Array.isArray(messageIds) || messageIds.length === 0) {
      throw new Error("messageIds должен быть непустым массивом");
    }

    console.log("👀 Отмечаем как прочитанные сообщения:", messageIds);

    const user = await authStateReady();

    if (!user) {
      throw new Error("Пользователь не авторизован");
    }

    const token = await user.getIdToken();

    const response = await axios.post(
      `${API_BASE_URL}/mark_messages_as_read`,
      {
        message_ids: messageIds
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }
    );

    console.log("✅ Сообщения помечены как прочитанные");
    console.log("📨 message_ids:", messageIds);
    console.log("📦 backend response:", response.data);

    return { success: true };
  } catch (error) {
    console.error("❌ Ошибка при отметке сообщений как прочитанных");
    console.error("📨 message_ids:", messageIds);
    console.error(error);

    return { success: false };
  }
}

/**
 * Получить вакансии, на которые откликнулся собеседник
 * @param {string} userId — ID владельца вакансий (текущий пользователь)
 * @param {string} interlocutorId — ID собеседника
 * @returns {Promise<Array>} — Массив вакансий
 */
static async getVacanciesRespondedByInterlocutor(userId, interlocutorId) {
  try {
    if (!userId || !interlocutorId) {
      throw new Error("userId и interlocutorId обязательны");
    }

    const response = await axios.get(`${API_BASE_URL}/get_vacancies_responded_by_interlocutor`, {
      params: {
        user_id: userId,
        interlocutor_id: interlocutorId
      },
      headers: {
        "Content-Type": "application/json"
      }
    });

    console.log("✅ Получены вакансии, на которые откликнулся собеседник:", response.data.vacancies);

    return response.data.vacancies || [];
  } catch (error) {
    console.error("❌ Ошибка получения откликов от собеседника:", error);
    return [];
  }
}

static async deleteChat(interlocutorId, chatId) {
  try {
    if (!interlocutorId || !chatId) {
      throw new Error(
        "interlocutorId и chatId обязательны"
      );
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error(
        "Пользователь не авторизован"
      );
    }

    const userId = user.uid;
    const token = await user.getIdToken(true);

    console.log("🗑 DELETE CHAT REQUEST:", {
      userId,
      interlocutorId,
      chatId
    });

    const response = await axios.post(
      `${API_BASE_URL}/delete_chat`,
      {
        user_id: userId,
        interlocutor_id: interlocutorId,
        chat_id: chatId
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }
    );

    console.log(
      "✅ Чат и сообщения успешно удалены:",
      response.data
    );

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error(
      "❌ Ошибка при удалении чата:",
      error
    );

    return {
      success: false,
      error
    };
  }
}

static async deleteMessage(messageId, force = false) {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Пользователь не авторизован");

      const token = await user.getIdToken(true);

      const response = await axios.post(
        'https://europe-west3-hyper-e122e.cloudfunctions.net/delete_message',
        {
          message_id: messageId,
          force: force,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (err) {
      console.error("❌ Ошибка при удалении сообщения:", err);
      return { success: false, error: err.message || 'Unknown error' };
    }
  }

}

export default ChatsService;